import type { Graphics } from 'pixi.js';
import { PAPER, lerpColor } from '../../color';
import type { WorldDress, WorldFx } from '../../world';

const TAU = Math.PI * 2;
const ARMS = 6;
const RUNES = 6;
const SNOW = [-0.42, -0.18, 0.3, -0.5, 0.12, -0.22, 0.48, -0.3, -0.05, 0.4, -0.3, 0.08, 0.24, 0.15, 0.55, 0.06];
const GROUND = 0.4;

function flake(g: Graphics, x: number, y: number, r: number, rot: number, sx = 1, sy = 1): void {
  for (let i = 0; i < ARMS; i++) {
    const a = rot + (i / ARMS) * TAU;
    const ux = Math.cos(a);
    const uy = Math.sin(a);
    g.moveTo(x, y).lineTo(x + ux * r * sx, y + uy * r * sy);
    const bx = x + ux * r * 0.55 * sx;
    const by = y + uy * r * 0.55 * sy;
    for (const side of [-0.75, 0.75]) {
      const b = a + side;
      g.moveTo(bx, by).lineTo(bx + Math.cos(b) * r * 0.3 * sx, by + Math.sin(b) * r * 0.3 * sy);
    }
  }
}

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    const { g, pal } = k;
    const r = body.radius;
    k.arcLine(body, r * 0.8, 0, TAU);
    g.stroke({ width: Math.max(1, r * 0.05), color: pal.rim, alpha: 0.3 * alpha });
    flake(g, body.x, body.y, r * 0.6, 0.1, body.scaleX, body.scaleY);
    g.stroke({ width: Math.max(1, r * 0.065), color: lerpColor(color, pal.ink, 0.5), alpha: 0.5 * alpha, cap: 'round' });
    k.rimLight(body, 0.55 * alpha);
  },

  mineCircle(k, body, alpha, width, fill) {
    const { g, pal } = k;
    const r = body.radius;
    const sx = body.scaleX;
    const sy = body.scaleY;
    k.trace(body);
    g.fill({ color: fill, alpha });
    const a0 = Math.asin(GROUND);
    k.arcLine(body, r * 0.97, a0, Math.PI - a0);
    g.closePath().fill({ color: pal.heldLight, alpha: 0.75 * alpha });
    const tree = lerpColor(fill, pal.heldShade, 0.85);
    g.poly([body.x, body.y - 0.26 * r * sy, body.x - 0.15 * r * sx, body.y + 0.08 * r * sy, body.x + 0.15 * r * sx, body.y + 0.08 * r * sy]);
    g.poly([body.x, body.y - 0.06 * r * sy, body.x - 0.21 * r * sx, body.y + 0.4 * r * sy, body.x + 0.21 * r * sx, body.y + 0.4 * r * sy]);
    g.fill({ color: tree, alpha: 0.85 * alpha });
    for (let i = 0; i < SNOW.length; i += 2) g.circle(body.x + SNOW[i]! * r * sx, body.y + SNOW[i + 1]! * r * sy, Math.max(1, r * 0.04));
    g.fill({ color: PAPER, alpha: 0.85 * alpha });
    k.arcLine(body, r * 0.78, -2.6, -1.5);
    g.stroke({ width: Math.max(1.5, r * 0.08), color: pal.heldLight, alpha: 0.8 * alpha, cap: 'round' });
    k.trace(body);
    g.stroke({ width: width * 0.8, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    k.trace(body);
    g.fill({ color: lerpColor(pal.bg, 0x000000, 0.4), alpha: 0.5 * alpha });
    k.trace(body);
    g.fill({ color: pal.incoming.circle, alpha: 0.07 * alpha });
    g.circle(body.x, body.y, reach).stroke({ width: Math.max(1.5, k.unit * 0.007), color: pal.incoming.circle, alpha: 0.45 * alpha });
    g.circle(body.x, body.y, reach * 1.07).stroke({ width: 1, color: pal.rim, alpha: 0.22 * alpha });
    const s = Math.max(3.5, k.unit * 0.026);
    k.ringDots(body, reach, RUNES, spin, (x, y, _i, a) => {
      flake(g, x, y, s, a);
    });
    g.stroke({ width: Math.max(1, s * 0.24), color: pal.spot, alpha: 0.85 * alpha, cap: 'round' });
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.85 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    g.circle(x, y, radius * 1.25);
    if (on) g.fill({ color: last ? pal.spot : lerpColor(pal.rim, pal.incoming.circle, 0.5), alpha });
    g.stroke({ width: width * 0.8, color: pal.rim, alpha: (on ? 0.9 : 0.5) * alpha });
    if (!on) return;
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI + Math.PI / 6;
      const dx = Math.cos(a) * radius * 0.75;
      const dy = Math.sin(a) * radius * 0.75;
      g.moveTo(x - dx, y - dy).lineTo(x + dx, y + dy);
    }
    g.stroke({ width: Math.max(1, width * 0.4), color: pal.ink, alpha: 0.45 * alpha, cap: 'round' });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    const step = Math.max(6, k.unit * 0.035);
    const off = Math.max(2.5, k.unit * 0.012);
    for (let i = 2; i < count; i += 2) {
      const x0 = points[i - 2]!;
      const y0 = points[i - 1]!;
      const dx = points[i]! - x0;
      const dy = points[i + 1]! - y0;
      const len = Math.hypot(dx, dy);
      if (len <= 0) continue;
      const nx = (-dy / len) * off;
      const ny = (dx / len) * off;
      const n = Math.min(80, Math.floor(len / step));
      for (let s = 1; s < n; s++) {
        const t = s / n;
        const x = x0 + dx * t;
        const y = y0 + dy * t;
        g.circle(x + nx, y + ny, 1.2).circle(x - nx, y - ny, 1.2);
      }
    }
    g.fill({ color: pal.path, alpha: alpha * 2.4 });
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, i, color, alpha, rot) {
    if (i % 3 === 0) {
      const c = Math.cos(rot);
      const s = Math.sin(rot);
      const a = size * 1.3;
      const b = size * 0.55;
      g.poly([x + c * a, y + s * a, x - s * b, y + c * b, x - c * a, y - s * a, x + s * b, y - c * b]);
    } else {
      g.star(x, y, 6, size * 1.3, size * 0.42, rot);
    }
    g.fill({ color, alpha });
  },

  milestone(g, m, pal) {
    g.circle(m.x, m.y, m.r).stroke({ width: m.width, color: pal.rim, alpha: m.fade * 0.5 });
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * TAU + 0.2;
      const r0 = m.r * 1.04;
      const r1 = m.r * (i % 2 === 0 ? 1.14 : 1.09);
      g.moveTo(m.x + Math.cos(a) * r0, m.y + Math.sin(a) * r0).lineTo(m.x + Math.cos(a) * r1, m.y + Math.sin(a) * r1);
    }
    g.stroke({ width: m.width * 0.6, color: pal.incoming.circle, alpha: m.fade * 0.4, cap: 'round' });
    const a0 = -Math.PI * 0.95;
    const a1 = a0 + Math.PI * (0.55 + 0.6 * m.grow);
    const r1 = m.r * 1.26;
    const r2 = m.r * 1.36;
    g.moveTo(m.x + Math.cos(a0) * r1, m.y + Math.sin(a0) * r1).arc(m.x, m.y, r1, a0, a1);
    g.stroke({ width: m.width * 1.8, color: pal.held, alpha: m.fade * 0.32, cap: 'round' });
    g.moveTo(m.x + Math.cos(a0 + 0.3) * r2, m.y + Math.sin(a0 + 0.3) * r2).arc(m.x, m.y, r2, a0 + 0.3, a1 + 0.2);
    g.stroke({ width: m.width, color: pal.spot, alpha: m.fade * 0.26, cap: 'round' });
  },

  trail(g, x, y, size, angle) {
    g.star(x, y, 6, size * 1.2, size * 0.4, angle);
  },
};
